import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, LoaderCircle, Pencil, Save, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";

const WEBSITE_URL = (process.env.REACT_APP_WEBSITE_URL || "")
  .trim()
  .replace(/\/+$/, "");

const EMPTY_FORM = {
  meta_title: "",
  meta_description: "",
};

const SeoMetadata = () => {
  const { showErrorToast, showSuccessToast } = useToast();
  const [pages, setPages] = useState([]);
  const [search, setSearch] = useState("");
  const [section, setSection] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const loadPages = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/seo/pages");
      setPages(response.data?.data?.pages || []);
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Failed to load the SEO page list",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, []);

  const sections = useMemo(
    () => ["All", ...Array.from(new Set(pages.map((page) => page.section)))],
    [pages],
  );

  const visiblePages = useMemo(() => {
    const query = search.trim().toLowerCase();

    return pages.filter((page) => {
      const matchesSection = section === "All" || page.section === section;
      const matchesSearch =
        !query ||
        page.page_label.toLowerCase().includes(query) ||
        page.path.toLowerCase().includes(query) ||
        (page.meta_title || "").toLowerCase().includes(query) ||
        (page.meta_description || "").toLowerCase().includes(query);

      return matchesSection && matchesSearch;
    });
  }, [pages, search, section]);

  const openEditModal = (page) => {
    setEditingPage(page);
    setForm({
      meta_title: page.meta_title || "",
      meta_description: page.meta_description || "",
    });
  };

  const closeEditModal = () => {
    if (isSaving) return;
    setEditingPage(null);
    setForm(EMPTY_FORM);
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveMetadata = async (event) => {
    event.preventDefault();
    if (!editingPage || isSaving) return;

    setIsSaving(true);
    try {
      const response = await apiClient.put(
        `/seo/pages/${editingPage.page_key}`,
        form,
      );
      const saved = response.data?.data?.item || {
        ...editingPage,
        ...form,
      };

      setPages((current) =>
        current.map((page) =>
          page.page_key === editingPage.page_key ? saved : page,
        ),
      );
      showSuccessToast(`${editingPage.page_label} metadata updated`);
      setEditingPage(null);
      setForm(EMPTY_FORM);
    } catch (err) {
      showErrorToast(
        err.response?.data?.message ||
          `Unable to save ${editingPage.page_label}`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">
          SEO Metadata
        </h1>
        <p className="mt-1 text-sm font-medium text-slate-500">
          Complete static page list. Use Edit to manage the meta title and meta
          description for each public URL.
        </p>
      </div>

      <div className="rounded-xl border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur-xl">
        <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_minmax(220px,0.45fr)_auto]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search page, URL, title or description"
              className="rounded-lg pl-10"
            />
          </label>
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="h-10 min-w-[220px] bg-white text-slate-700 shadow-sm">
              <SelectValue placeholder="Filter by section" />
            </SelectTrigger>
            <SelectContent>
              {sections.map((item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center justify-end text-sm font-semibold text-slate-500">
            {visiblePages.length} of {pages.length} pages
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-xl border border-white/60 bg-white/80 py-16 text-center text-sm font-medium text-slate-500">
          <LoaderCircle className="mx-auto mb-3 h-5 w-5 animate-spin" />
          Loading complete page list...
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px] border-collapse text-left">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                    Page
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                    Public URL
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                    Meta Title
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                    Meta Description
                  </th>
                  <th className="sticky right-0 bg-slate-50 px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visiblePages.length ? (
                  visiblePages.map((page) => (
                    <tr
                      key={page.page_key}
                      className="group transition-colors hover:bg-slate-50/80"
                    >
                      <td className="px-5 py-4 align-top">
                        <div className="font-bold text-slate-900">
                          {page.page_label}
                        </div>
                        <span className="mt-1.5 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          {page.section}
                        </span>
                      </td>
                      <td className="px-5 py-4 align-top">
                        {WEBSITE_URL ? (
                          <a
                            href={`${WEBSITE_URL}${page.path}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex max-w-[220px] items-start gap-1.5 break-all text-sm font-semibold text-primary hover:underline"
                          >
                            {page.path}
                            <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          </a>
                        ) : (
                          <span className="inline-block max-w-[220px] break-all text-sm font-medium text-slate-500">
                            {page.path}
                          </span>
                        )}
                      </td>
                      <td className="max-w-[280px] px-5 py-4 align-top">
                        {page.meta_title ? (
                          <p className="line-clamp-2 text-sm font-semibold leading-6 text-slate-700">
                            {page.meta_title}
                          </p>
                        ) : (
                          <span className="text-sm italic text-slate-400">
                            Automatic fallback
                          </span>
                        )}
                      </td>
                      <td className="max-w-[360px] px-5 py-4 align-top">
                        {page.meta_description ? (
                          <p className="line-clamp-3 text-sm leading-6 text-slate-600">
                            {page.meta_description}
                          </p>
                        ) : (
                          <span className="text-sm italic text-slate-400">
                            Automatic fallback
                          </span>
                        )}
                      </td>
                      <td className="sticky right-0 bg-white px-5 py-4 text-right align-top group-hover:bg-slate-50">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => openEditModal(page)}
                          className="rounded-lg"
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-14 text-center text-sm font-medium text-slate-500"
                    >
                      No SEO pages match the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog
        open={Boolean(editingPage)}
        onOpenChange={(open) => (open ? null : closeEditModal())}
      >
        <DialogContent className="w-[calc(100%_-_2rem)] rounded-xl border border-white/60 bg-white p-0 sm:max-w-2xl">
          <form onSubmit={saveMetadata}>
            <DialogHeader className="border-b border-slate-100 px-6 py-5 pr-12">
              <DialogTitle>Edit SEO Metadata</DialogTitle>
              <DialogDescription>
                {editingPage
                  ? `${editingPage.page_label} · ${editingPage.path}`
                  : "Update page search metadata"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 px-6 py-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <label
                    htmlFor="seo-meta-title"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Meta Title
                  </label>
                  <span className="text-xs text-slate-400">
                    {form.meta_title.length}/255
                  </span>
                </div>
                <Input
                  id="seo-meta-title"
                  value={form.meta_title}
                  onChange={(event) =>
                    updateForm("meta_title", event.target.value)
                  }
                  maxLength={255}
                  placeholder="Page title shown in search results"
                  className="rounded-lg"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <label
                    htmlFor="seo-meta-description"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Meta Description
                  </label>
                  <span className="text-xs text-slate-400">
                    {form.meta_description.length}/1000
                  </span>
                </div>
                <Textarea
                  id="seo-meta-description"
                  value={form.meta_description}
                  onChange={(event) =>
                    updateForm("meta_description", event.target.value)
                  }
                  maxLength={1000}
                  rows={6}
                  placeholder="Short description shown in search results"
                  className="min-h-[150px] resize-y rounded-lg"
                />
              </div>

              <p className="rounded-lg bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500">
                Leave either field blank to use that public page&apos;s automatic
                fallback metadata.
              </p>
            </div>

            <DialogFooter className="gap-2 border-t border-slate-100 px-6 py-5 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={closeEditModal}
                disabled={isSaving}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving} className="rounded-lg">
                {isSaving ? (
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {isSaving ? "Saving..." : "Save Metadata"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeoMetadata;
